import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "container-fluid admin-page" }
const _hoisted_2 = { class: "form-group" }
const _hoisted_3 = ["readonly"]
const _hoisted_4 = { class: "form-group" }
const _hoisted_5 = { class: "form-group" }
const _hoisted_6 = { class: "icon-options mt4" }
const _hoisted_7 = ["src", "onClick"]
const _hoisted_8 = { class: "form-group" }
const _hoisted_9 = { class: "form-group errors" }
const _hoisted_10 = { class: "p1" }
const _hoisted_11 = ["innerHTML"]

import type { AxiosError } from 'axios';
import commentTypeApi from 'src/apis/commentType';
import {
  computed,
  onMounted,
  reactive,
} from 'vue';
import type { CommentType } from 'src/models/master';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import { useReactiveMasters } from 'src/composables/useMasters';

interface AdminCommentTypeEdit {
  id: number;
  group1Id?: number;
  commentType: string;
  commentTypeDisp: string;
  dispOrder: number;
  iconPath: string;
}

interface AdminCommentTypeState {
  showEditModal: boolean;
  editMode: 'edit' | 'create';
  commentTypes: CommentType[];
  editting: AdminCommentTypeEdit;
  httpErrorMsgs: string[];
  iconPaths: string[];
  showDestroyModal: boolean;
  showErrorModal: boolean;
  errorModalMsg: string;
}

const destroyModalTitle = '削除';
const destroyModalMsg = 'を削除してもよろしいですか？';
const errorModalTitle = 'エラー';


export default /*@__PURE__*/_defineComponent({
  __name: 'CommentTypes',
  setup(__props) {

const initEdittingState = (): AdminCommentTypeEdit => ({
  id: 0,
  commentType: '',
  commentTypeDisp: '',
  dispOrder: 0,
  iconPath: '',
});
const state = reactive<AdminCommentTypeState>({
  showEditModal: false,
  editMode: 'edit',
  commentTypes: [],
  editting: initEdittingState(),
  httpErrorMsgs: [],
  iconPaths: [],
  showDestroyModal: false,
  showErrorModal: false,
  errorModalMsg: '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。',
});

const canSave = computed<boolean>(() => {
  const hasCommentType = !!state.editting.commentType;
  const hasCommentTypeDisp = !!state.editting.commentTypeDisp;
  const hasIconPath = !!state.editting.iconPath;
  const hasNumericDispOrder = !!state.editting.dispOrder &&
    /^\d+$/.test(state.editting.dispOrder.toString());
  return hasCommentType && hasCommentTypeDisp && hasIconPath && hasNumericDispOrder;
});
const editModalTitle = computed<string>(() => {
  return state.editMode === 'create' ? '新規作成' : '編集';
});

const { masters, resolveMastersPromise, refreshCommentType } = useReactiveMasters();

onMounted(async() => {
  await resolveMastersPromise();
  state.iconPaths = masters.comment.commentTypeIconPaths;
  doSearch();
});

const doSearch = async() => {
  const commentTypes = await commentTypeApi.adminIndex();
  state.commentTypes = commentTypes;
};
const selectIcon = (path: string) => {
  state.editting.iconPath = path;
};
const edit = (obj: CommentType) => {
  state.editting = { ...obj };
  state.httpErrorMsgs = [];
  state.editMode = 'edit';
  state.showEditModal = true;
};
const create = () => {
  const unusedIconPaths = state.commentTypes.reduce((iconPaths: string[], commentType) => {
    return iconPaths.filter(iconPath => iconPath !== commentType.iconPath);
  }, state.iconPaths.slice());
  const maxDispOrder = state.commentTypes.reduce((max, curr) => {
    return curr.dispOrder > max ? curr.dispOrder : max;
  }, 0);
  state.editting = {
    id: 0,
    commentType: '',
    commentTypeDisp: '',
    iconPath: unusedIconPaths.length > 0 ? unusedIconPaths[0] : state.iconPaths[0],
    dispOrder: maxDispOrder + 1,
  };
  state.editMode = 'create';
  state.showEditModal = true;
};
const save = async() => {
  try {
    if (state.editMode === 'edit') {
      await commentTypeApi.adminUpdate(state.editting.id, {
        comment_type_disp: state.editting.commentTypeDisp,
        icon_path: state.editting.iconPath,
        disp_order: state.editting.dispOrder,
      });
    } else {
      await commentTypeApi.adminCreate({
        comment_type: state.editting.commentType,
        comment_type_disp: state.editting.commentTypeDisp,
        icon_path: state.editting.iconPath,
        disp_order: state.editting.dispOrder,
      });
    }
    state.showEditModal = false;
    searchAndRefreshCommentTypeMaster();
  } catch (err) {
    state.httpErrorMsgs = getErrorMessages(err as AxiosError);
  }
};
const tryDestroy = (obj: CommentType) => {
  state.editting = obj;
  state.showDestroyModal = true;
};
const doDestroy = async() => {
  try {
    await commentTypeApi.adminDestroy(state.editting.id);
    state.showDestroyModal = false;
    searchAndRefreshCommentTypeMaster();
  } catch (err) {
    const errMsgs = getErrorMessages(err as AxiosError);
    if (errMsgs.includes('has comments')) {
      state.errorModalMsg = '1件以上の付箋が登録されているので削除できません。';
    } else if (errMsgs.includes('last comment_type')) {
      state.errorModalMsg = '最後の付箋種別は削除できません。';
    } else {
      state.errorModalMsg = '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。';
    }
    state.showDestroyModal = false;
    state.showErrorModal = true;
  }
};
const searchAndRefreshCommentTypeMaster = async() => {
  await doSearch();
  refreshCommentType(state.commentTypes);
};

const fields = [
  { name: 'commentType', label: '種別名' },
  { name: 'commentTypeDisp', label: '表示名' },
  { name: 'iconPath', label: 'アイコン', type: 'icon' },
  { name: 'dispOrder', label: '表示順' },
];

return (_ctx: any,_cache: any) => {
  const _component_AdminListTable = _resolveComponent("AdminListTable")!
  const _component_Modal = _resolveComponent("Modal")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", { class: "title-area" }, [
      _cache[7] || (_cache[7] = _createElementVNode("h2", null, "付箋種別管理", -1)),
      _createElementVNode("div", { class: "create-btn" }, [
        _createElementVNode("button", {
          class: "btn btn-info",
          onClick: create
        }, [...(_cache[6] || (_cache[6] = [
          _createElementVNode("i", { class: "fas fa-plus" }, null, -1),
          _createElementVNode("span", { class: "ml-1" }, "新規作成", -1)
        ]))])
      ])
    ]),
    _createVNode(_component_AdminListTable, {
      "display-data": state.commentTypes,
      fields: fields,
      onStartEdit: edit,
      onStartDestroy: tryDestroy
    }, null, 8, ["display-data"]),
    (state.showEditModal)
      ? (_openBlock(), _createBlock(_component_Modal, {
          key: 0,
          title: editModalTitle.value,
          "bottom-close-button-label": "保存",
          isCloseButtonDisabled: !canSave.value,
          onClose: save,
          onDismiss: _cache[3] || (_cache[3] = ($event: any) => (state.showEditModal = false))
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", null, [
              _createElementVNode("div", _hoisted_2, [
                _cache[8] || (_cache[8] = _createElementVNode("label", { class: "form-control-label" }, " 種別名 ", -1)),
                _withDirectives(_createElementVNode("input", {
                  type: "text",
                  class: "form-control",
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((state.editting.commentType) = $event)),
                  readonly: state.editMode === 'edit'
                }, null, 8, _hoisted_3), [
                  [_vModelText, state.editting.commentType]
                ])
              ]),
              _createElementVNode("div", _hoisted_4, [
                _cache[9] || (_cache[9] = _createElementVNode("label", { class: "form-control-label" }, " 表示名 ", -1)),
                _withDirectives(_createElementVNode("input", {
                  type: "text",
                  class: "form-control",
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((state.editting.commentTypeDisp) = $event))
                }, null, 512), [
                  [_vModelText, state.editting.commentTypeDisp]
                ])
              ]),
              _createElementVNode("div", _hoisted_5, [
                _cache[10] || (_cache[10] = _createElementVNode("label", { class: "form-control-label" }, " アイコン ", -1)),
                _createElementVNode("div", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.iconPaths, (e) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: e,
                      class: _normalizeClass(["icon-box", { 'selected': e === state.editting.iconPath }])
                    }, [
                      _createElementVNode("img", {
                        class: "resource-icon",
                        src: e,
                        onClick: ($event: any) => (selectIcon(e))
                      }, null, 8, _hoisted_7)
                    ], 2))
                  }), 128))
                ])
              ]),
              _createElementVNode("div", _hoisted_8, [
                _cache[11] || (_cache[11] = _createElementVNode("label", { class: "form-control-label" }, " 表示順 ", -1)),
                _withDirectives(_createElementVNode("input", {
                  type: "text",
                  class: "form-control",
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((state.editting.dispOrder) = $event))
                }, null, 512), [
                  [_vModelText, state.editting.dispOrder]
                ])
              ]),
              _createElementVNode("div", _hoisted_9, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.httpErrorMsgs, (e, i) => {
                  return (_openBlock(), _createElementBlock("div", {
                    class: "error",
                    key: i
                  }, _toDisplayString(e), 1))
                }), 128))
              ])
            ])
          ]),
          _: 1
        }, 8, ["title", "isCloseButtonDisabled"]))
      : _createCommentVNode("", true),
    (state.showDestroyModal)
      ? (_openBlock(), _createBlock(_component_Modal, {
          key: 1,
          title: destroyModalTitle,
          "show-bottom-dismiss-button": true,
          onClose: doDestroy,
          onDismiss: _cache[4] || (_cache[4] = ($event: any) => (state.showDestroyModal = false))
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", null, [
              _createElementVNode("p", _hoisted_10, [
                _cache[12] || (_cache[12] = _createTextVNode("付箋種別", -1)),
                _cache[13] || (_cache[13] = _createElementVNode("span", { class: "colon" }, null, -1)),
                _createTextVNode(_toDisplayString(state.editting.commentTypeDisp), 1)
              ]),
              _createElementVNode("p", {
                class: "p2 font-mtx-desc",
                innerHTML: destroyModalMsg
              })
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (state.showErrorModal)
      ? (_openBlock(), _createBlock(_component_Modal, {
          key: 2,
          title: errorModalTitle,
          "show-bottom-dismiss-button": false,
          onClose: _cache[5] || (_cache[5] = ($event: any) => (state.showErrorModal = false))
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", null, [
              _createElementVNode("p", {
                class: "p2 font-mtx-desc",
                innerHTML: state.errorModalMsg
              }, null, 8, _hoisted_11)
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})